'use client';

import { Elements } from '@stripe/react-stripe-js';
import { type StripeElementsOptions, type StripeElementsOptionsMode } from '@stripe/stripe-js';
import { type JSX, type ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { FontSizes, FontWeights, Text } from '@calm-web/design-system';

import Loader from '@/components/Loader';
import paymentElementsFormRefreshMessages from '@/components/purchase/PaymentElementsFormRefresh/messages';
import {
	desktopStripeAppearanceOptions,
	stripeAppearanceOptions,
} from '@/components/purchase/StripeElementsProvider/utils';
import { useAnalytics } from '@/hooks/analytics';
import { useAppLoadedState } from '@/hooks/store';
import useIsDesktop from '@/hooks/ui/useIsDesktop';
import { useIsMobile } from '@/hooks/ui/useIsMobile';
import genericMessages from '@/messages/messages';
import { STRIPE_ZERO_DECIMAL_CURRENCIES } from '@/utils/prices/constants';

import CancelAnytimeLink from '../../CancelAnytimeLink';
import { useFormatPricePlusApplicableTaxes, useScreenDidMount } from '../../hooks';
import PaymentMethod from '../../PaymentMethod';
import PaymentPlan from '../../PaymentPlan';
import { useSisu } from '../../Provider';
import { ValuePropsListWrapper } from '../../styles';
import { isFormattedMessage, type MessageNode } from '../../utils';
import loadStripeOnce from './loadStripeOnce';
import {
	CTAContainer,
	PaymentPageWrap,
	PaymentWrapper,
	PurchaseFormSubWrapper,
	PurchaseFormWrapper,
	TwoColumnWrapper,
} from './styles';

export type PaymentScreenProps = {
	/**
	 * @defaultValue
	 * ```tsx
	 * "sisu-payment"
	 * ```
	 */
	'data-testid'?: string;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	branding?: ReactElement | null;

	/**
	 * @defaultValue
	 * ```tsx
	 * "right"
	 * ```
	 */
	brandingPosition?: 'left' | 'right';

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	contentBasedImage?: ReactElement | null;

	elementsOptions?: StripeElementsOptionsMode;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	header?: ReactElement | null;

	paymentMethod?: ReactElement;

	paymentPlan?: ReactElement;

	stripePublishableKey?: string;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	title?: MessageNode | null;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	subtitle?: MessageNode | null;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	valuePropsList?: ReactElement | null;

	terms?: MessageNode | null;

	onScreenDidMount?: () => void;
};

export default function Payment({
	'data-testid': testId = 'sisu-payment',

	branding = null,
	brandingPosition = 'right',

	contentBasedImage = null,
	elementsOptions,
	header = null,

	paymentMethod = <PaymentMethod />,
	paymentPlan = <PaymentPlan />,

	stripePublishableKey,

	title = null,
	subtitle = null,

	valuePropsList = null,

	terms,

	onScreenDidMount,
}: PaymentScreenProps) {
	const appLoaded = useAppLoadedState();
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const {
		activeScreen,
		backgroundColor,
		flowName,
		hasFreeTrial,
		isWithinModal,
		logPageViewedEvent,
		selectedSku,
	} = useSisu();
	const isDesktop = useIsDesktop();
	const [isMobile] = useIsMobile();
	const formatPricePlusApplicableTaxes = useFormatPricePlusApplicableTaxes();

	let stripe: ReturnType<typeof loadStripeOnce> | null = null;
	if (stripePublishableKey) {
		stripe = loadStripeOnce(stripePublishableKey);
	}

	const currency = selectedSku?.currency?.toLowerCase() ?? 'usd';
	let amount = 0;
	if (!hasFreeTrial) {
		if (selectedSku.finalPrice) {
			// convert from milli unit to centi unit
			// e.g. 69990 milli USD -> 6999 centi USD ($69.99)
			amount = Math.round(selectedSku.finalPrice / 10);
			if (STRIPE_ZERO_DECIMAL_CURRENCIES.has(currency)) {
				// strip decimals off
				// e.g. 330000 centi KRW -> 3300 KRW (₩3,300)
				// e.g. 30000 centi JPY -> 300 JPY (¥300)
				amount = Math.round(amount / 100);
			}
		} else {
			amount = 6999;
		}
	}

	useScreenDidMount('payment', () => {
		logPageViewedEvent('payment');
		logEvent({
			eventName: 'Subscribe : Purchase : Form : Landed',
			eventProps: {
				source: flowName,
				is_stripe_next: true,
				includes_quiz_recommendation: false,
			},
		});
		onScreenDidMount?.();
	});

	if (typeof terms === 'undefined') {
		if (selectedSku) {
			const price = formatPricePlusApplicableTaxes(
				selectedSku.finalPrice,
				selectedSku.currency,
				selectedSku.country,
			);
			const cancelAnytimeLink = (chunk: string[]): JSX.Element => (
				<CancelAnytimeLink>{chunk}</CancelAnytimeLink>
			);
			if (selectedSku.trialDuration) {
				terms = formatMessage(paymentElementsFormRefreshMessages.freeTrialTerms, {
					price,
					cancelAnytimeLink,
				});
			} else {
				let cadence;
				if (selectedSku.duration === 'year') {
					cadence = formatMessage(genericMessages.yearly);
				} else if (selectedSku.duration === 'month') {
					cadence = formatMessage(genericMessages.monthly);
				}
				terms = formatMessage(paymentElementsFormRefreshMessages.noTrialTerms, {
					price,
					cadence,
					cancelAnytimeLink,
				});
			}
		}
	}

	if (activeScreen !== 'payment') {
		return null;
	}

	// stripe-js 9's `StripeElementsOptions` is a discriminated union on `mode`; construct
	// each branch explicitly so TS narrows it (no cast). `amount` belongs only to the
	// `payment` mode — the free-trial path uses `mode: 'setup'` (a SetupIntent, no amount).
	const appearance = isDesktop ? desktopStripeAppearanceOptions : stripeAppearanceOptions;
	const elementsProviderOptions: StripeElementsOptions = hasFreeTrial
		? { ...elementsOptions, mode: 'setup', currency, appearance, paymentMethodCreation: 'manual' }
		: { ...elementsOptions, mode: 'payment', amount, currency, appearance, paymentMethodCreation: 'manual' };

	return (
		<Elements options={elementsProviderOptions} stripe={stripe}>
			{!branding && header}
			<PaymentPageWrap
				backgroundColor={backgroundColor}
				data-testid={testId}
				isCompact={isWithinModal}
				isWithinModal={isWithinModal}
			>
				<TwoColumnWrapper isWithinModal={isWithinModal}>
					{valuePropsList && (
						<ValuePropsListWrapper showHeader={!!header} isWithinModal={isWithinModal}>
							{(brandingPosition === 'left' || isMobile) && !header && branding}
							{(!isMobile || !isWithinModal) && contentBasedImage}
							{title && isFormattedMessage(title) ? (
								<Text align="center" el="h1" size={FontSizes['2xl']} weight={FontWeights.Medium}>
									{title}
								</Text>
							) : (
								title
							)}
							{subtitle && isFormattedMessage(subtitle) ? (
								<Text align="center" el="h2" size={FontSizes.lg} weight={FontWeights.Medium}>
									{subtitle}
								</Text>
							) : (
								subtitle
							)}
							{!isMobile && valuePropsList}
						</ValuePropsListWrapper>
					)}
					<PaymentWrapper $overflow={isWithinModal && !isMobile}>
						{brandingPosition === 'right' && !isMobile && !header && branding}
						<PurchaseFormWrapper>
							<PurchaseFormSubWrapper>
								{appLoaded ? (
									<>
										{paymentPlan}
										{paymentMethod}
									</>
								) : (
									<Loader />
								)}
							</PurchaseFormSubWrapper>
						</PurchaseFormWrapper>
						{terms && (
							<CTAContainer>
								{isFormattedMessage(terms) ? (
									<Text color="blackAlpha60" el="p" size={FontSizes.sm}>
										{terms}
									</Text>
								) : (
									terms
								)}
							</CTAContainer>
						)}
					</PaymentWrapper>
				</TwoColumnWrapper>
				{isMobile && valuePropsList}
			</PaymentPageWrap>
		</Elements>
	);
}
